import firebase from "firebase/compat/app";
import { Base } from "../base";
import { camelToSnakeCase, isNullOrUndefined, isObject } from "@pineapplelab/util";
import { timestampToDate as convertTimestamp } from "../../utils/utils";

export abstract class FBase extends Base {

  private __createdAt = firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp;
  private __updatedAt = firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp;
  deletedAt: Object = null;
  createdBy: string = null;
  updatedBy: string = null;
  deletedBy: string = null;
  isDeleted: boolean = false;
  isHidden: boolean = false;
  id: string = null;

  set createdAt(timestamp: any) {
    this.__createdAt = timestamp;
  }

  get createdAt() {
    if (this.__createdAt instanceof firebase.firestore.FieldValue) {
      return null;
    }
    return this.__createdAt;
  }

  set updatedAt(timestamp: any) {
    this.__updatedAt = timestamp;
  }

  get updatedAt() {
    if (this.__updatedAt instanceof firebase.firestore.FieldValue) {
      return null;
    }
    return this.__updatedAt;
  }



  /**
  * Keep in mind that the class properties initialization will overwrite all values set here!
  *
  * Call fromJson() method from your subclass constructor instead.
  *
  * Example:
  * ```typescript
    export class User extends FBase {

      firstName: string = null;
      ....

      constructor(json?: { [key: string]: any }) {
        super(); // don't pass json here if you'll call this.fromJson(json)

        if (isObject(json)) {
          this.fromJson(json);
        }
      }

    }
  * ```
  *
  */
  constructor(json?: { [key: string]: any }) {
    super();

    if (isObject(json)) {
      this.fromJson(json);
    }

  }

  toJson(camelCase = false) {
    const json = super.toJson(camelCase);
    const createdAtValue = (this as any)['_createdAt'] || (this as any)['__createdAt'];
    if (createdAtValue instanceof firebase.firestore.FieldValue && isNullOrUndefined(createdAtValue)) {
      json.created_at = createdAtValue;
    } else {
      json.updated_at = firebase.firestore.FieldValue.serverTimestamp();
    }
    if (!json.is_deleted) {
      json.is_deleted = false;
    }
    this.dateToTimestamp(json);
    return json;
  }

  	/**
	 * The function "getMetadata" returns an array of string values representing date properties, this mapping the firebase Timestamp and Date
	 * @returns an array of strings.
	 */
	public getMetadata(): string[] {
		const DATE_PROP_KEY = 'dateProperties';
		return Reflect.getMetadata(DATE_PROP_KEY, this) || [];
	}

	/**
	 * The function converts a JSON object to a TypeScript object and also converts a timestamp property
	 * to a Date object.
	 * @param json - A JSON object with string keys and values of any type.
	 * @returns The return value of the function is the result of calling the `super.fromJson(json)`
	 * method.
	 */
	fromJson(json: { [key: string]: any; }) {
		this.timestampToDate(json)
		return super.fromJson(json);
	}

  	/**
	 * The function converts timestamp values in a JSON object to Date objects.
	 * @param json - The `json` parameter is an object that contains key-value pairs.
	 */
	timestampToDate(json: { [key: string]: any }) {
		this.getMetadata()
			.map(meta => camelToSnakeCase(meta))
			.forEach((prop: any) => {
				json[prop] = convertTimestamp(json[prop]);
			});
	}


	/**
	 * The function converts date properties in a JSON object to Firebase Firestore Timestamp instances.
	 * @param json - An object containing key-value pairs, where the values can be of any type.
	 */
	dateToTimestamp(json: { [key: string]: any }) {

		this.getMetadata()
			.map(meta => camelToSnakeCase(meta))
			.forEach(prop => {

				let timestampInstance: firebase.firestore.Timestamp = null;

				if (json[prop] instanceof Date) {
					timestampInstance = firebase.firestore.Timestamp.fromDate(json[prop])
				}

				if (typeof json[prop] == 'string') {
					timestampInstance = firebase.firestore.Timestamp.fromDate(new Date(json[prop]))
				}

				json[prop] = timestampInstance;
			});

	}
}
